/**
 * PageLayout Component
 *
 * Main page wrapper that handles ALL visual structure for pages:
 * - Page background color (colorNeutralBackground2)
 * - Full height layout (100vh minimum)
 * - Header section with title, subtitle, and tabs (via AppPageHeader)
 * - Content section with 24px padding
 *
 * Pages should only pass data and configuration - no styling code.
 */

import { ReactNode } from 'react';
import { makeStyles, tokens } from '@fluentui/react-components';
import { AppPageHeader, TabConfig } from '../../templates/AppPageHeader';

const useStyles = makeStyles({
  container: {
    backgroundColor: tokens.colorNeutralBackground2,
    minHeight: '100vh',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
});

export interface PageLayoutProps {
  /** Page title displayed in the header */
  title: string;
  /** Optional subtitle/description below the title */
  subtitle?: string;
  /** Optional tab configuration for page navigation */
  tabs?: TabConfig[];
  /** Currently selected tab value */
  selectedTab?: string;
  /** Callback when tab changes */
  onTabChange?: (value: string) => void;
  /** Optional content to render in the upper right corner of the header */
  headerActions?: ReactNode;
  /** Callback when back button is clicked - shows back button when provided */
  onBack?: () => void;
  /** Page content */
  children: ReactNode;
  /** Test ID for the component */
  testId?: string;
}

export function PageLayout({
  title,
  subtitle,
  tabs,
  selectedTab,
  onTabChange,
  headerActions,
  onBack,
  children,
  testId,
}: PageLayoutProps) {
  const styles = useStyles();

  return (
    <div className={styles.container} data-testid={testId}>
      <AppPageHeader
        title={title}
        subtitle={subtitle}
        tabs={tabs}
        selectedTab={selectedTab}
        onTabChange={onTabChange}
        headerActions={headerActions}
        onBack={onBack}
      />
      <div className={styles.content}>{children}</div>
    </div>
  );
}
